/**
 * Custom hook for Firebase Authentication
 */

import { useState, useEffect } from 'react';
import { User, signInWithCustomToken, signOut as firebaseSignOut, onAuthStateChanged, browserLocalPersistence, setPersistence } from 'firebase/auth';
import { auth } from '../services/firebase';
import { FUNCTIONS_BASE_URL } from '../services/funkcje';
import { pobierzTokenLogowania } from '../services/logowanieWp';

// Set persistence to LOCAL to persist across browser windows/tabs
setPersistence(auth, browserLocalPersistence).catch(console.error);

export function useAuth() {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      setUser(user);
      setLoading(false);
      // Clear any stale errors when auth state changes
      if (user) {
        setError(null);
      }
    });

    return () => unsubscribe();
  }, []);

  const signIn = async (login: string, password: string) => {
    try {
      setError(null);
      setLoading(true);
      const token = await pobierzTokenLogowania(login, password, `${FUNCTIONS_BASE_URL}/silviaLogin`);
      await signInWithCustomToken(auth, token);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to sign in');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const signOut = async () => {
    try {
      setError(null);
      await firebaseSignOut(auth);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to sign out');
      throw err;
    }
  };

  return { user, loading, error, signIn, signOut };
}
